import React, { forwardRef } from 'react';
import styled from 'styled-components/native';
import { Controller } from 'react-hook-form';
import {
  Platform,
  View,
  Switch,
  TextInput as BaseTextInput,
  Pressable,
  ScrollView
} from 'react-native';
import { Text, Hint } from 'app/src/styles';
import colors, { elGray } from 'app/src/styles/colors';
import { elRed } from 'app/src/styles/colors';
import fonts from 'app/src/styles/fonts';
import { LinkSpace } from 'app/src/elements';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSquareXmark } from '@fortawesome/free-solid-svg-icons';

import { Picker } from './Picker';
import { DateTimePicker } from './DateTimePicker';
import { openPickerUnlessDateTextClick } from './DateInput';
import { abbreviations } from './states';
import moment from 'moment';
import { STATUSES } from '../components/Sales/elements';

export const StyledTextInput = styled(BaseTextInput)`
  border-width: 1px;
  border-color: black;
  padding: 6px;
  background-color: #fff;
`;

export const StyledPicker = styled(Picker)`
  border-color: black;
  padding: 3px;
  padding-top: 6px;
  padding-bottom: 6px;
  font-size: 14px;
`;

const ErrorText = styled(Text)`
  padding-left: 10px;
  color: red;
`;

export const Field = styled(View)`
  ${props => props.inline ? `
    flex: 1;
  ` : `
    margin-bottom: 10px;
  `}

  ${props => props.width ? `max-width: ${props.width}px;` : ''}

  ${props => (props.inline && props.errors) ? `
    border-width: 2px;
    border-color: red;
  ` : ''
  }
`;

export const SwitchField = styled(View)`
  flex-direction: row;
  margin-top: 5px;
  margin-bottom: 10px;
  align-items: center;
`;

const SwitchLabel = styled(Text)`
  margin-left: 10px;
`;

export const errorsFor = (errors, name) => {
  if (!errors) {
    return null;
  }

  name.split('.').forEach(element => {
    if (errors?.[element]) {
      errors = errors[element];
    } else {
      errors = null;
    }
  });

  return errors;
};

export const FieldLabel = ({ name, label, hint, errors }) => {
  let errorMessage;

  if (errors?.[name]) {
    errorMessage = errors[name].message || 'Required';
  }

  return (
    <>
      <View style={{flexDirection: 'row'}}>
        <Text>{label}</Text>
        {errorMessage && <ErrorText>{errorMessage}.</ErrorText>}
      </View>
      {hint && <Hint>{hint}</Hint>}
    </>
  );
};

export const TextInput = forwardRef((props, ref) => {
  const {
    label,
    hint,
    control,
    errors,
    placeholder,
    secureTextEntry,
    multiline,
    rows,
    inline,
    width,
    ...controllerProps
  } = props;

  return (
    <Field inline={inline} width={width} errors={errorsFor(errors, props.name)}>
      {!inline && (
        <FieldLabel
          name={props.name}
          label={label}
          hint={hint}
          errors={errors}
        />
      )}
      <Controller
        {...controllerProps}
        control={control}
        render={({ field }) => (
          <StyledTextInput
            {...field}
            ref={ref}
            secureTextEntry={secureTextEntry}
            placeholder={placeholder}
            onChangeText={field.onChange}
            multiline={multiline}
            rows={rows}
          />
        )}
      />
    </Field>
  );
});

export const TextAreaInput = forwardRef((props, ref) => {
  const {
    label,
    hint,
    control,
    errors,
    placeholder,
    secureTextEntry,
    rows,
    inline,
    ...controllerProps
  } = props;

  return (
    <Field inline={inline} errors={errorsFor(errors, props.name)}>
      <FieldLabel name={props.name} label={label} hint={hint} errors={errors} />
      <Controller
        {...controllerProps}
        control={control}
        render={({ field }) => (
          <textarea {...field} rows={rows || 4} />
        )}
      />
    </Field>
  );
});

export const CheckboxInput = forwardRef((props, ref) => {
  const {
    label,
    hint,
    control,
    errors,
    checked,
    inline,
    ...controllerProps
  } = props;

  return (
    <Field inline={inline} errors={errorsFor(errors, props.name)}>
      <View style={{display: 'flex', flexDirection: 'row'}}>
        {!inline && (
          <FieldLabel
            name={props.name}
            label={label}
            hint={hint}
            errors={errors}
          />
        )}
        <Controller
          {...controllerProps}
          control={control}
          render={({ field }) => (
            <input
              type='checkbox'
              checked={field.value}
              onChange={field.onChange}
            />
          )}
        />
      </View>
    </Field>
  );
});

export const DateTimeInput = forwardRef((props, ref) => {
  const {
    label,
    hint,
    control,
    errors,
    options,
    inline,
    ...controllerProps
  } = props;

  return (
    <Field inline={inline} errors={errorsFor(errors, props.name)}>
      <FieldLabel name={props.name} label={label} hint={hint} errors={errors} />
      <Controller
        {...controllerProps}
        control={control}
        render={({ field }) => {
          const onChange = (e, valueOnNative) => {
            if ('web' === Platform.OS) {
              field.onChange(e.target.value);
            } else {
              field.onChange(valueOnNative);
            }
          };
          return (
            <DateTimePicker
              {...field}
              type="datetime-local"
              mode="datetime"
              onChange={onChange}
              ref={ref}
            />
          );
        }}
      />
    </Field>
  );
});

export const DateInput = forwardRef((props, ref) => {
  const {
    label,
    hint,
    control,
    errors,
    options,
    inline,
    ...controllerProps
  } = props;

  return (
    <Field inline={inline} errors={errorsFor(errors, props.name)}>
      {!inline && (
        <FieldLabel
          name={props.name}
          label={label}
          hint={hint}
          errors={errors}
        />
      )}
      <Controller
        {...controllerProps}
        control={control}
        render={({ field }) => {
          const onChange = (e, valueOnNative) => {
            if ('web' === Platform.OS) {
              field.onChange(e.target.value);
            } else {
              field.onChange(valueOnNative);
            }
          };
          return (
            <DateTimePicker
              {...field}
              type="date"
              mode="date"
              onChange={onChange}
              onClick={
                'web' === Platform.OS ? openPickerUnlessDateTextClick : undefined
              }
              ref={ref}
            />
          );
        }}
      />
    </Field>
  );
});

export const TimeInput = forwardRef((props, ref) => {
  const {
    label,
    hint,
    control,
    errors,
    options,
    inline,
    ...controllerProps
  } = props;

  return (
    <Field inline={inline} errors={errorsFor(errors, props.name)}>
      <FieldLabel name={props.name} label={label} hint={hint} errors={errors} />
      <Controller
        {...controllerProps}
        control={control}
        render={({ field }) => {
          const onChange = (e, valueOnNative) => {
            if ('web' === Platform.OS) {
              field.onChange(e.target.value);
            } else {
              field.onChange(valueOnNative);
            }
          };
          return (
            <DateTimePicker
              {...field}
              type="time"
              mode="time"
              onChange={onChange}
              ref={ref}
            />
          );
        }}
      />
    </Field>
  );
});

export const PickerInput = props => {
  const {
    label,
    hint,
    control,
    errors,
    options,
    noPlaceholder,
    inline,
    handleChange,
    width,
    ...controllerProps
  } = props;

  return (
    <Field inline={inline} errors={errorsFor(errors, props.name)} width={width}>
      {!inline && (
        <FieldLabel
          name={props.name}
          label={label}
          hint={hint}
          errors={errors}
        />
      )}
      <Controller
        {...controllerProps}
        control={control}
        render={({ field }) => (
          <StyledPicker
            {...field}
            selectedValue={field.value}
            onChange={e => {
              field.onChange(e);
              if (handleChange) { handleChange(e.target.value); }
            }}
            onValueChange={field.onChange}
          >
            {!noPlaceholder &&
              <Picker.Item
                key='picker-default'
                value=""
                label='Select'
              />
            }
            {options?.map(option => (
              <Picker.Item
                key={option.value}
                value={option.value}
                label={option.label}
              />
            ))}
          </StyledPicker>
        )}
      />
    </Field>
  );
};

export const StatePickerInput = props => {
  const {
    label,
    hint,
    control,
    errors,
    options,
    inline,
    ...controllerProps
  } = props;

  return (
    <Field inline={inline} errors={errorsFor(errors, props.name)}>
      <FieldLabel name={props.name} label={label} hint={hint} errors={errors} />
      <Controller
        {...controllerProps}
        control={control}
        render={({ field }) => (
          <StyledPicker
            {...field}
            selectedValue={field.value}
            onValueChange={field.onChange}
          >
            <Picker.Item
              value=""
              label="Select"
            />
            {abbreviations.map(option => (
              <Picker.Item
                key={option}
                value={option}
                label={option}
              />
            ))}
          </StyledPicker>
        )}
      />
    </Field>
  );
};

export const SwitchInput = props => {
  const {
    label,
    control,
    inline,
    errors,
    disabled,
    ...controllerProps
  } = props;

  return (
    <SwitchField inline={inline} errors={errorsFor(errors, props.name)}>
      <Controller
        {...controllerProps}
        control={control}
        render={({ field }) => (
          <Switch
            {...field}
            activeTrackColor={colors.lightButton}
            activeThumbColor={colors.button}
            onValueChange={field.onChange}
            disabled={disabled}
          />
        )}
      />
      <SwitchLabel>{label}</SwitchLabel>
    </SwitchField>
  );
};

export const FormattedPhoneInput = forwardRef((props, ref) => {
  const {
    label,
    hint,
    control,
    errors,
    placeholder,
    secureTextEntry,
    inline,
    ...controllerProps
  } = props;

  return (
    <Field inline={inline} errors={errorsFor(errors, props.name)}>
      <FieldLabel name={props.name} label={label} hint={hint} errors={errors} />
      <Controller
        {...controllerProps}
        control={control}
        render={({ field }) => (
          <StyledTextInput
            {...field}
            ref={ref}
            secureTextEntry={secureTextEntry}
            placeholder={placeholder}
            onChangeText={field.onChange}
          />
        )}
      />
    </Field>
    // const standardizedPhone = value => {
    //   if (value) {
    //     return '+1' + value.replace(/[-()/\s+/]/g, '');
    //   }

    //   return null;
    // };
  )
})

const USER_ROLES = [
  'Super Admin',
  'Admin',
  'Accounting',
  'Lot',
  'Auctioneer',
  'Auction Clerk',
  'Developer',
];

export const RolePickerInput = props => {
  const {
    label,
    hint,
    control,
    errors,
    options,
    inline,
    ...controllerProps
  } = props;

  return (
    <Field inline={inline} errors={errorsFor(errors, props.name)}>
      <FieldLabel name={props.name} label={label} hint={hint} errors={errors} />
      <Controller
        {...controllerProps}
        control={control}
        render={({ field }) => (
          <StyledPicker
            {...field}
            selectedValue={field.value}
            onValueChange={field.onChange}
          >
            {USER_ROLES.map(option => (
              <Picker.Item
                key={option}
                value={option}
                label={option}
              />
            ))}
          </StyledPicker>
        )}
      />
    </Field>
  );
};

export const YearPickerInput = props => {
  const {
    label,
    hint,
    control,
    errors,
    options,
    inline,
    ...controllerProps
  } = props;

  let finalYear = 2099; // The customer requested this be hard-coded.
  let years = [];
  for(let x = 0; x < 200; x++) {
    years.push(finalYear - x);
  }

  return (
    <Field inline={inline} errors={errorsFor(errors, props.name)}>
      <FieldLabel name={props.name} label={label} hint={hint} errors={errors} />
      <Controller
        {...controllerProps}
        control={control}
        render={({ field }) => (
          <StyledPicker
            {...field}
            selectedValue={field.value}
            onValueChange={field.onChange}
          >
            {years.map(option => (
              <Picker.Item
                key={`year${option}`}
                value={parseInt(option)}
                label={option}
              />
            ))}
          </StyledPicker>
        )}
      />
    </Field>
  );
};

export const StatusPickerInput = props => {
  const {
    label,
    hint,
    control,
    errors,
    options,
    inline,
    ...controllerProps
  } = props;

  return (
    <Field inline={inline} errors={errorsFor(errors, props.name)}>
      <FieldLabel name={props.name} label={label} hint={hint} errors={errors} />
      <Controller
        {...controllerProps}
        control={control}
        render={({ field }) => (
          <StyledPicker
            {...field}
            selectedValue={field.value}
            onValueChange={field.onChange}
          >
            {STATUSES.map(option => (
              <Picker.Item
                key={`status${option}`}
                value={option}
                label={option}
              />
            ))}
          </StyledPicker>
        )}
      />
    </Field>
  );
};

export const CurrencyInput = forwardRef((props, ref) => {
  const {
    label,
    hint,
    control,
    errors,
    placeholder,
    secureTextEntry,
    inline,
    ...controllerProps
  } = props;

  return (
    <Field inline={inline} errors={errorsFor(errors, props.name)}>
      <FieldLabel name={props.name} label={label} hint={hint} errors={errors} />
      <Controller
        {...controllerProps}
        control={control}
        render={({ field }) => (
          <StyledTextInput
            {...field}
            ref={ref}
            type='number'
            secureTextEntry={secureTextEntry}
            placeholder={placeholder}
            onChangeText={field.onChange}
          />
        )}
      />
    </Field>
  );
});

export const NumberInput = forwardRef((props, ref) => {
  const {
    label,
    hint,
    control,
    errors,
    placeholder,
    secureTextEntry,
    inline,
    ...controllerProps
  } = props;

  return (
    <Field inline={inline} errors={errorsFor(errors, props.name)}>
      <FieldLabel name={props.name} label={label} hint={hint} errors={errors} />
      <Controller
        {...controllerProps}
        control={control}
        render={({ field }) => (
          <StyledTextInput
            {...field}
            ref={ref}
            type='number'
            secureTextEntry={secureTextEntry}
            placeholder={placeholder}
            onChangeText={field.onChange}
          />
        )}
      />
    </Field>
  );
});

export const salespersonRadioButtons = (vendorOrUser, setVendorOrUser, setValue) => {
  return (
    <View
      style={{
        display: 'flex',
        justifyContent: 'flex-start',
        flexDirection: 'row',
        marginBottom: '10px'
      }}
    >
      <Pressable
        onPress={() => {
          setValue('userId', '')
          setVendorOrUser('Vendor');
        }
      }
      >
        <View style={{
          height: 18,
          width: 18,
          borderRadius: 12,
          borderWidth: 2,
          borderColor: elRed,
          alignItems: 'center',
          justifyContent: 'center',
        }}>
          {
            vendorOrUser === 'Vendor' ?
                <View style={{
                  height: 8,
                  width: 8,
                  borderRadius: 6,
                  backgroundColor: elRed,
                }}/>
              : null
          }
        </View>
      </Pressable>
      <Text>&nbsp;&nbsp;Vendor</Text>
      <LinkSpace />
      <Pressable
        onPress={() => {
          setValue('vendorId', '');
          setVendorOrUser('User');
        }}
      >
        <View style={{
          height: 18,
          width: 18,
          borderRadius: 12,
          borderWidth: 2,
          borderColor: elRed,
          alignItems: 'center',
          justifyContent: 'center',
        }}>
          {
            vendorOrUser === 'User' ?
                <View style={{
                  height: 8,
                  width: 8,
                  borderRadius: 6,
                  backgroundColor: elRed,
                }}/>
                : null
              }
        </View>
      </Pressable>
      <Text>&nbsp;&nbsp;User</Text>
    </View>
  );
}

const MultiSelectInput = (props) => {
  const {selected, setSelected, resourceType, resources} = props;
  const filteredResources = resources?.filter(
    resource => !selected?.includes(resource)
  ) || []

  const UsersContainer = styled(View)`
    display: flex;
    flex-wrap: wrap;
    flex-direction: row;
    width: 600px;
  `;

  const UserButtonsContainer = styled(View)`
    display: flex;
    flex-wrap: wrap;
    flex-direction: row;
    border: solid 4px ${elGray}
    border-radius: 5px;
    padding: 20px;
    overflow-x: wrap;
  `;

  const UserSelectCard = styled(View)`
    border: 1px solid black;
    width: fit-content;
    padding: 5px;
    border-radius: 5px;
    margin: 5px;
    display: flex;
    flex-direction: row;
    background-color: #fff;
  `;

  const LabelContainer = styled(View)`
    display: flex;
    flex-direction: row;
  `;

  const InputLabel = styled(Text)`
    font-weight: bold;
    font-size: 18px;
    color: ${elRed}
  `;

  const addToArray = (resource) => {
    let newSelected = [...selected, resource.id]
    setSelected(newSelected);
  }

  const removeFromArray = (clickedResourceId) => {
    let newSelected = selected?.filter(resourceId => {
      if(resourceId !== clickedResourceId) {
        return resourceId;
      }
    })
    setSelected(newSelected);
  }

  return (
    <View
      style={{
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: '#fff',
        padding: '10px',
        border: '1px solid black'
      }}
    >
      <LabelContainer>
        <InputLabel>{resourceType}</InputLabel>
        {!selected?.length ?
          <View style={{marginLeft: '5px', marginTop: '2px'}}>
            <Text>(click on the names you wish to add)</Text>
          </View> :
          <Pressable
            onPress={() => setSelected([])}
            style={{marginLeft: '5px', marginTop: '2px'}}
          >
            <Text>clear all</Text>
          </Pressable>
        }
      </LabelContainer>
      <UsersContainer>
        {resources && resources.length ?
          resources?.map(resource => {
            if(selected.includes(resource.id)) {
              return (
                <UserSelectCard key={resource.id}>
                  <Text>{resource.name}</Text>
                  <Pressable>
                    <FontAwesomeIcon
                      icon={faSquareXmark}
                      size='lg'
                      style={{
                        color: elRed,
                        marginLeft: '5px'
                      }}
                      onClick={() => removeFromArray(resource.id)}
                    />
                  </Pressable>
                </UserSelectCard>
              )
            }
          }) :
          null
        }
      </UsersContainer>
      {filteredResources && filteredResources.length > 0 &&
        <UserButtonsContainer>
          {filteredResources?.map(resource => {
            if(!selected.includes(resource.id)) {
              return (
                <View key={resource.id}>
                  <Pressable
                    onPress={() => addToArray(resource)}
                  >
                    <UserSelectCard>
                      <Text>{resource.name}</Text>
                    </UserSelectCard>
                  </Pressable>
                </View>
              )
            }
          })}
        </UserButtonsContainer>
      }
    </View>
  )
}

const Input = {
  Text: TextInput,
  TextArea: TextAreaInput,
  Date: DateInput,
  Time: TimeInput,
  DateTime: DateTimeInput,
  Picker: PickerInput,
  StatePicker: StatePickerInput,
  Switch: SwitchInput,
  Phone: FormattedPhoneInput,
  RolePicker: RolePickerInput,
  YearPicker: YearPickerInput,
  StatusPicker: StatusPickerInput,
  Checkbox: CheckboxInput,
  Currency: CurrencyInput,
  Number: NumberInput,
  MultiSelect: MultiSelectInput
};

export default Input;
